import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { app } from './firebase-config';
import { useNavigate } from 'react-router-dom';
import { SmartImage, shrinkImage, uploadThumbnails } from './imageUtils';


const db = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);

const PublicProfile = () => {
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('attended');
  const [showPointsTable, setShowPointsTable] = useState(false);
  const [showHostPointsTable, setShowHostPointsTable] = useState(false);
  const [eventTypeSummary, setEventTypeSummary] = useState({});
  const [profileImage, setProfileImage] = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const navigate = useNavigate();


  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const authUser = auth.currentUser;
        if (authUser) {
          const currentDoc = await getDoc(doc(db, 'users', authUser.uid));
          if (currentDoc.exists()) setCurrentUserName(currentDoc.data().name || '');
        }

        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
          if (userDoc.data().profileImageUrl) setProfileImage(userDoc.data().profileImageUrl);
        }

        const eventDocs = await getDocs(collection(db, 'events'));
        const eventsList = eventDocs.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setEvents(eventsList);
      } catch (error) {
        console.error('Error fetching public profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  const handleAdminUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const user = auth.currentUser;
  if (!user) return;

  // 🟢 Check by UID instead of name
  if (user.uid !== 'PAA7WTgXPvfoEWhHFNtujwuxL4p1') {
    alert('Bare Matias (admin) kan endre andres bilder.');
    return;
  }

  const small = await shrinkImage(file, 1024);
  const fileRef = ref(storage, `profile_pictures/${userId}/${small.name}`);
  const uploadTask = uploadBytesResumable(fileRef, small);

  uploadTask.on(
    'state_changed',
    null,
    (error) => console.error('Feil ved opplasting:', error),
    async () => {
      const url = await getDownloadURL(uploadTask.snapshot.ref);
      await uploadThumbnails(storage, fileRef.fullPath, small, [200]);
      await updateDoc(doc(db, 'users', userId), { profileImageUrl: url });
      setProfileImage(url);
      alert('Profilbilde oppdatert!');
    }
  );
};


  useEffect(() => {
    if (!userData || events.length === 0) return;

    const attendedEvents = events.filter((e) => e.Deltagelse.includes(userData.name));
    const summary = {};
    attendedEvents.forEach((e) => {
      const type = e.event_type;
      if (!summary[type]) summary[type] = { count: 0, points: 0 };
      summary[type].count += 1;
      if (type === 'Blåtur🌍') summary[type].points += 7;
      else if (type === 'KD🧠') summary[type].points += 5;
      else if (type === 'KL🥩' || type === 'Vino🍷') summary[type].points += 4;
    });
    setEventTypeSummary(summary);
  }, [userData, events]);

  if (loading) return <div className="publicprofile-loading">Laster profil...</div>;
  if (!userData) return <div className="publicprofile-error">Ingen profil funnet.</div>;

  const attended = events.filter((e) => e.Deltagelse.includes(userData.name));
  const hosted = events.filter((e) => Array.isArray(e.Vert) && e.Vert.includes(userData.name));

  const individualPointsMap = { 'Vino🍷': 4, 'KL🥩': 4, 'KD🧠': 5, 'Blåtur🌍': 7 };
  const computedHostPoints = hosted.reduce(
    (sum, e) => sum + (individualPointsMap[e.event_type] || 0),
    0
  );

  return (
    <div className="publicprofile-container">
      <button
        onClick={() => {
          if (window.history.state && window.history.state.idx > 0) {
            navigate(-1); // Go back to previous page
          } else {
            navigate('/profiler'); // Fallback if user came from an external link
          }
        }}
        className="button-17"
        style={{
          marginBottom: '20px',
          backgroundColor: '#444',
          color: 'white',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        ← Tilbake
      </button>

      {profileImage ? (
        <SmartImage
          src={profileImage}
          size={200}
          alt="Profil"
          style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            objectFit: 'cover',
            marginBottom: '10px',
          }}
        />
      ) : (
        <div
          style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            backgroundColor: '#999',
            margin: '0 auto 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '12px',
          }}
        >
          Ingen bilde
        </div>
      )}
      {currentUserName === 'Matias' && (
        <label className="button-18 file-input-label">
          Velg fil
          <input type="file" accept="image/png, image/jpeg" onChange={handleAdminUpload} hidden />
        </label>
      )}

      <h1>Profil: {userData.name}</h1>

      <div className="profile-summary">
        <div>
          <strong>Deltakelsespoeng:</strong> {userData.points || 0}
          <span className="subtext"> ({attended.length} deltagelser)</span>
        </div>
        <button
          className="button-17"
          onClick={() => setShowPointsTable((prev) => !prev)}
        >
          {showPointsTable ? 'Skjul beregning for deltakerpoeng' : 'Vis beregning for deltakerpoeng'}
        </button>
          {showPointsTable && (
            <table className="profile-table points-summary-table">
              <thead>
                <tr>
                  <th>Event type</th>
                  <th>Poeng per event</th>
                  <th>Antall</th>
                  <th>Deltakerpoeng</th>
                </tr>
              </thead>
              <tbody>
                {['Vino🍷', 'KL🥩', 'KD🧠', 'Blåtur🌍'].map((type) => (
                  <tr key={type}>
                    <td>{type}</td>
                    <td>{individualPointsMap[type]}</td>
                    <td>{eventTypeSummary[type]?.count || 0}</td>
                    <td>{eventTypeSummary[type]?.points || 0}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold' }}>
                  <td>Totalt</td>
                  <td>-</td>
                  <td>
                    {Object.values(eventTypeSummary).reduce(
                      (sum, t) => sum + (t?.count || 0),
                      0
                    )}
                  </td>
                  <td>
                    {Object.values(eventTypeSummary).reduce(
                      (sum, t) => sum + (t?.points || 0),
                      0
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
      </div>

      <div className="profile-summary">
        <div>
          <strong>Arrangørpoeng:</strong> {computedHostPoints}
          <span className="subtext"> ({hosted.length} arrangementer)</span>
        </div>
        <button
          className="button-17"
          onClick={() => setShowHostPointsTable((prev) => !prev)}
        >
          {showHostPointsTable
            ? 'Skjul beregning for arrangørpoeng'
            : 'Vis beregning for arrangørpoeng'}
        </button>
        
        {showHostPointsTable && (
        <table className="profile-table hosted-events-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Type</th>
              <th>Kvartal</th>
              <th>Vertpoeng</th>
            </tr>
          </thead>
          <tbody>
            {hosted.map((e) => (
              <tr key={e.id}>
                <td>{e.sted_konsept}</td>
                <td>{e.event_type}</td>
                <td>{e.Kvartal}</td>
                <td>{individualPointsMap[e.event_type] || 0}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3" style={{ fontWeight: 'bold' }}>Totalt</td>
              <td style={{ fontWeight: 'bold' }}>
                {computedHostPoints}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
      </div>

      <div className="button-container">
        <button
          className={`button-17${activeSection === 'attended' ? '' : ' muted'}`}
          onClick={() => setActiveSection('attended')}
        >
          Se deltatte events
        </button>
        <button
          className={`button-17${activeSection === 'hosted' ? '' : ' muted'}`}
          onClick={() => setActiveSection('hosted')}
        >
          Se arrangerte events
        </button>
        <button
          className={`button-17${activeSection === 'awards' ? '' : ' muted'}`}
          onClick={() => setActiveSection('awards')}
        >
          Se arrangørpriser
        </button>
      </div>

 
      <div className="section-content">
        {activeSection === 'attended' && (
          <>
            {attended.length > 0 ? (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Type</th>
                    <th>Kvartal</th>
                  </tr>
                </thead>
                <tbody>
                  {attended.map((e) => (
                    <tr key={e.id}>
                      <td>{e.sted_konsept}</td>
                      <td>{e.event_type}</td>
                      <td>{e.Kvartal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>Ingen registrerte deltagelser.</p>
            )}
          </>
        )}

        {activeSection === 'hosted' && (
          <>
            {hosted.length > 0 ? (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Type</th>
                    <th>Kvartal</th>
                  </tr>
                </thead>
                <tbody>
                  {hosted.map((e) => (
                    <tr key={e.id}>
                      <td>{e.sted_konsept}</td>
                      <td>{e.event_type}</td>
                      <td>{e.Kvartal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>Ingen arrangerte events.</p>
            )}
          </>
        )}

        {activeSection === 'awards' && (
          <>
            {userData.awards?.length > 0 ? (
              <ul className="awards-list">
                {userData.awards.map((award, index) => (
                  <li key={index}>{award}</li>
                ))}
              </ul>
            ) : (
              <p>Ingen priser registrert.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;